import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { subscriptionsApi } from "../queries/subscriptions"
import { leadsApi } from "../queries/lead"
import { TabRangeState, EngagementsTab } from "../../types"
import { DateRangePreset } from "../../common/ui/activity/date-filter"

export const engagementsSliceName = "engagements"

export interface EngagementsState {
  tabs: {
    range: TabRangeState
  }
  counts: {
    engagements: number | null
    leads: number | null
  }
}

// Calculate 120 days ago from now
const now = new Date()
const oneHundredTwentyDaysAgo = new Date(now)
oneHundredTwentyDaysAgo.setDate(now.getDate() - 120)
oneHundredTwentyDaysAgo.setHours(0, 0, 0, 0) // Start of day

const initialState: EngagementsState = {
  tabs: {
    range: Object.values(EngagementsTab).reduce((acc, key) => {
      // Default 120 days for leads, engagements, dreamRate; 30 days for activity
      if (
        key === EngagementsTab.leads ||
        key === EngagementsTab.engagements ||
        key === EngagementsTab.dreamRate
      ) {
        acc[key] = {
          range: "custom",
          customStartDate: oneHundredTwentyDaysAgo,
          customEndDate: now
        }
      } else if (key === EngagementsTab.activity) {
        acc[key] = {
          range: "last_30",
          customStartDate: null,
          customEndDate: null
        }
      } else {
        acc[key] = {
          range: "last_7",
          customStartDate: null,
          customEndDate: null
        }
      }
      return acc
    }, {} as TabRangeState)
  },
  counts: {
    engagements: null,
    leads: null
  }
}

const engagementsSlice = createSlice({
  name: engagementsSliceName,
  initialState,
  reducers: {
    updateRange: (
      state,
      action: PayloadAction<{
        tabKey: EngagementsTab
        range: DateRangePreset
        customStartDate?: Date | null
        customEndDate?: Date | null
      }>
    ) => {
      const { tabKey, range, customStartDate, customEndDate } = action.payload
      state.tabs.range[tabKey] = {
        range,
        customStartDate: customStartDate || null,
        customEndDate: customEndDate || null
      }
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      subscriptionsApi.endpoints.fetchSubscriptionLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.engagements = action.payload?.length ?? 0
      }
    )
    builder.addMatcher(
      leadsApi.endpoints.fetchLeadLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.leads = action.payload?.length ?? 0
      }
    )
  }
})

const { reducer, actions } = engagementsSlice

export const { updateRange } = actions

export const selectTabRange = (state: RootState, tabKey: EngagementsTab) =>
  state.engagements.tabs.range[tabKey]

export const selectHasEngagements = (state: RootState) =>
  (state.engagements.counts.engagements ?? 0) > 0

export const selectHasLeads = (state: RootState) =>
  (state.engagements.counts.leads ?? 0) > 0

export const engagementsReducer = reducer
